import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveIcon from '@mui/icons-material/Remove';
import { supabase } from '../config/supabase';
import { useAuditUserId } from '../auth/AuthContext';
import { lookupLabel } from '../data/lookups';
import { playerTierOptions, registrationStatusOptions, rosterStatusOptions, sexOptions, teamPriorityOptions } from '../data/options';
import type { LookupState, SortDirection, TeamGenderType, UserPreferences } from '../types';
import { comparePlayerNames, formatPlayerName } from '../utils/playerPreferences';
import { feeAssignmentSyncErrorMessage, syncTeamFeeAssignments } from '../utils/feeAssignments';
import { PlayerDisplayControls } from './PlayerDisplayControls';
import { SeasonTeamMultiSelect } from './SeasonTeamMultiSelect';
import { formatEdr } from '../utils/edr';
import { isDesignatedSubstitute, normalizeRosterRole, rosterRoleLabels, rosterRoleOptions, type RosterRole } from '../utils/rosterRoles';
import {
  compatibilityChipColor,
  compatibilityLevels,
  normalizePartnerPair,
  normalizedPartnerPreferencePayload,
  normalizePairingRule,
  pairingRuleChipColor,
  pairingRuleHelpText,
  pairingRuleLabels,
  pairingRuleLevels,
  partnerPreferenceKey,
  type PartnerCompatibility,
  type PartnerPairingRule,
} from '../utils/partnerPreferences';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';

type PlayerRow = {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  sex: string | null;
  ntrp_rating: number | null;
  estimated_dynamic_rating: number | null;
  is_active: boolean;
};

type RosterRow = {
  id: string;
  team_id: string;
  player_id: string;
  target_match_count: number | null;
  player_tier: string | null;
  roster_status: string;
  roster_role: RosterRole;
  team_priority: 'primary' | 'normal' | 'secondary';
  registration_status: string;
  registration_verified_at: string | null;
  registration_verified_by: string | null;
  registration_notes: string | null;
  include_substitute_in_fairness: boolean;
  is_active: boolean;
  notes: string | null;
  players?: PlayerRow | null;
};

type TeamDefaultRow = {
  id: string;
  default_target_match_count_per_player: number | null;
  team_gender_type: TeamGenderType | null;
};

type PartnerPreferenceRow = {
  id: string;
  workspace_id: string;
  team_id: string | null;
  player1_id: string;
  player2_id: string;
  compatibility: PartnerCompatibility | null;
  pairing_rule: PartnerPairingRule | null;
  is_active: boolean;
  notes: string | null;
  calculated_compatibility?: PartnerCompatibility | null;
  calculated_confidence?: string | null;
  compatibility_source?: string | null;
  review_status?: string | null;
  review_severity?: string | null;
  calculated_score?: number | null;
  calculated_match_count?: number | null;
  calculated_current_season_record?: string | null;
  calculated_average_game_differential?: number | null;
  calculated_match_tiebreak_record?: string | null;
  calculated_comeback_wins?: number | null;
  calculated_trend_direction?: string | null;
  calculated_match_story?: string | null;
  calculated_metrics?: {
    matchesWon?: number | null;
    matchesLost?: number | null;
    currentSeasonRecord?: { wins?: number | null; losses?: number | null } | null;
  } | null;
  calculated_at?: string | null;
};

type PartnerPreferenceDraft = {
  id: string | null;
  player1Id: string;
  player2Id: string;
  compatibility: PartnerCompatibility;
  pairingRule: PartnerPairingRule;
  isActive: boolean;
  notes: string;
};

type SeasonFilterRow = {
  id: string;
  season_name: string | null;
  year: number | null;
  start_date: string | null;
  end_date: string | null;
};

type RosterDraft = Pick<
  RosterRow,
  'target_match_count' | 'player_tier' | 'roster_status' | 'registration_status' | 'notes' | 'team_priority'
> & { roster_role: RosterRole };

type RosterPatch = Partial<Pick<
  RosterRow,
  'target_match_count' | 'player_tier' | 'roster_status' | 'roster_role' | 'registration_status' | 'registration_verified_at' | 'registration_verified_by' | 'notes' | 'team_priority'
>>;

type RosterManagerProps = {
  lookups: LookupState;
  refreshLookups: () => Promise<void>;
  userPreferences: UserPreferences;
  onUserPreferencesChange: (patch: Partial<UserPreferences>) => void;
  initialTeamId?: string | null;
  fixedTeamId?: string | null;
  embedded?: boolean;
  readOnly?: boolean;
  view?: 'all' | 'roster' | 'partner_preferences';
  canViewPartnerPreferences?: boolean;
  canEditPartnerPreferences?: boolean;
};

type Snack = {
  message: string;
  severity: 'success' | 'error';
};

type AvailableSortField = 'player_name' | 'sex' | 'ntrp_rating' | 'estimated_dynamic_rating';
type AssignedSortField = 'player_name' | 'sex' | 'ntrp_rating' | 'estimated_dynamic_rating' | 'target_match_count' | 'player_tier' | 'roster_status' | 'roster_role' | 'registration_status' | 'team_priority';
type RosterManagerViewState = {
  selectedTeamId: string;
  search: string;
  sexFilter: string;
  rosterRoleFilter: 'all' | RosterRole;
  assignedSearch: string;
  assignedRosterStatusFilter: string;
  assignedTierFilter: string;
  ntrpFilter: string[];
  partnerSearch: string;
  partnerCompatibilityFilter: string;
  partnerPairingRuleFilter: string;
  partnerCalculationFilter: string;
  partnerReviewFilter: string;
  partnerQuickFilter: string;
};

const ntrpOptions = ['2.5', '3.0', '3.5', '4.0', '4.5', '5.0', '5.5', '6.0', '6.5', '7.0'];

const tierLabels: Record<string, string> = {
  tier_1: 'Tier 1 - stronger players',
  tier_2: 'Tier 2 - middle players',
  tier_3: 'Tier 3 - developing players',
  tier_4: 'Tier 3 - developing players',
};

const rosterStatusLabels: Record<string, string> = {
  active: 'Active',
  substitute: 'Substitute',
  inactive: 'Inactive',
};

function registrationVerifiedLabel(value: string | null): string {
  if (!value) return 'Not verified';
  return new Date(value).toLocaleDateString();
}

function ntrpValue(player: PlayerRow): string {
  return typeof player.ntrp_rating === 'number' ? player.ntrp_rating.toFixed(1) : '';
}

function isMaleFemalePair(playerOne: PlayerRow | undefined, playerTwo: PlayerRow | undefined): boolean {
  const firstSex = playerOne?.sex || '';
  const secondSex = playerTwo?.sex || '';
  return (firstSex === 'male' && secondSex === 'female') || (firstSex === 'female' && secondSex === 'male');
}

function formatPairingRecord(wins: unknown, losses: unknown): string | null {
  if (typeof wins !== 'number' || typeof losses !== 'number') return null;
  return `${wins}-${losses}`;
}

function allTimePairingRecord(preference: PartnerPreferenceRow): string | null {
  return formatPairingRecord(preference.calculated_metrics?.matchesWon, preference.calculated_metrics?.matchesLost);
}

function currentSeasonPairingRecord(preference: PartnerPreferenceRow): string | null {
  return preference.calculated_current_season_record
    || formatPairingRecord(preference.calculated_metrics?.currentSeasonRecord?.wins, preference.calculated_metrics?.currentSeasonRecord?.losses);
}

export function RosterManager({ lookups, refreshLookups, userPreferences, onUserPreferencesChange, initialTeamId, fixedTeamId, embedded = false, readOnly = false, view = 'all', canViewPartnerPreferences = true, canEditPartnerPreferences = true }: RosterManagerProps) {
  const auditUserId = useAuditUserId();
  const defaultViewState = useMemo<RosterManagerViewState>(() => ({
    selectedTeamId: '',
    search: '',
    sexFilter: 'all',
    rosterRoleFilter: 'all',
    assignedSearch: '',
    assignedRosterStatusFilter: 'all',
    assignedTierFilter: 'all',
    ntrpFilter: [],
    partnerSearch: '',
    partnerCompatibilityFilter: 'all',
    partnerPairingRuleFilter: 'all',
    partnerCalculationFilter: 'all',
    partnerReviewFilter: 'all',
    partnerQuickFilter: 'all',
  }), []);
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState({
    userId: auditUserId,
    routeKey: embedded ? 'roster-manager:embedded' : 'roster-manager',
    contextKey: fixedTeamId || view,
    defaultState: defaultViewState,
  });
  const [selectedTeamId, setSelectedTeamId] = useState(fixedTeamId || initialTeamId || savedViewState.selectedTeamId);
  const [seasonRows, setSeasonRows] = useState<SeasonFilterRow[]>([]);
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [teamDefaults, setTeamDefaults] = useState<Map<string, number | null>>(() => new Map());
  const [teamGenderTypes, setTeamGenderTypes] = useState<Map<string, TeamGenderType>>(() => new Map());
  const [rosterRows, setRosterRows] = useState<RosterRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, RosterDraft>>({});
  const [selectedAvailable, setSelectedAvailable] = useState<string[]>([]);
  const [selectedAssigned, setSelectedAssigned] = useState<string[]>([]);
  const [search, setSearch] = useState(savedViewState.search);
  const [sexFilter, setSexFilter] = useState(savedViewState.sexFilter);
  const [rosterRoleFilter, setRosterRoleFilter] = useState<'all' | RosterRole>(savedViewState.rosterRoleFilter);
  const [assignedSearch, setAssignedSearch] = useState(savedViewState.assignedSearch);
  const [assignedRosterStatusFilter, setAssignedRosterStatusFilter] = useState(savedViewState.assignedRosterStatusFilter);
  const [assignedTierFilter, setAssignedTierFilter] = useState(savedViewState.assignedTierFilter);
  const [ntrpFilter, setNtrpFilter] = useState<string[]>(savedViewState.ntrpFilter);
  const [availableSort, setAvailableSort] = useState<{ field: AvailableSortField; direction: SortDirection }>({ field: 'player_name', direction: 'asc' });
  const [assignedSort, setAssignedSort] = useState<{ field: AssignedSortField; direction: SortDirection }>({ field: 'player_name', direction: 'asc' });
  const [loading, setLoading] = useState(false);
  const [savingRosterIds, setSavingRosterIds] = useState<Set<string>>(() => new Set());
  const [snack, setSnack] = useState<Snack | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addRosterRole, setAddRosterRole] = useState<RosterRole>('regular');
  const [partnerPreferences, setPartnerPreferences] = useState<PartnerPreferenceRow[]>([]);
  const [partnerSearch, setPartnerSearch] = useState(savedViewState.partnerSearch);
  const [partnerCompatibilityFilter, setPartnerCompatibilityFilter] = useState(savedViewState.partnerCompatibilityFilter);
  const [partnerPairingRuleFilter, setPartnerPairingRuleFilter] = useState(savedViewState.partnerPairingRuleFilter);
  const [partnerCalculationFilter, setPartnerCalculationFilter] = useState(savedViewState.partnerCalculationFilter);
  const [partnerReviewFilter, setPartnerReviewFilter] = useState(savedViewState.partnerReviewFilter);
  const [partnerQuickFilter, setPartnerQuickFilter] = useState(savedViewState.partnerQuickFilter);
  const [partnerDialogOpen, setPartnerDialogOpen] = useState(false);
  const [partnerDraft, setPartnerDraft] = useState<PartnerPreferenceDraft>({ id: null, player1Id: '', player2Id: '', compatibility: 'Unknown', pairingRule: 'none', isActive: true, notes: '' });
  const restoredViewStateKey = useRef('');
  const [viewStateReady, setViewStateReady] = useState(false);

  const playerById = useMemo(() => new Map(players.map((player) => [player.id, player])), [players]);
  const selectedTeamDefaultTarget = teamDefaults.get(selectedTeamId) ?? null;
  const selectedTeamGenderType = teamGenderTypes.get(selectedTeamId) || 'mixed';
  const selectedTeamOption = lookups.teams.find((team) => team.value === selectedTeamId);
  const selectedTeamName = selectedTeamOption?.label || 'Team';
  const selectedWorkspaceId = selectedTeamOption?.workspace_id || '';
  const activeRosterPlayerIds = useMemo(() => new Set(rosterRows.map((row) => row.player_id)), [rosterRows]);
  const rosterPreferencePlayers = useMemo(() => rosterRows.map((row) => row.players).filter(Boolean) as PlayerRow[], [rosterRows]);
  const currentSeasonId = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return seasonRows.find((season) => (!season.start_date || season.start_date <= today) && (!season.end_date || season.end_date >= today))?.id || '';
  }, [seasonRows]);

  usePersistentScrollRestoration({
    userId: auditUserId,
    routeKey: embedded ? 'roster-manager:embedded' : 'roster-manager',
    contextKey: fixedTeamId || view,
    ready: !loading,
  });

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setSelectedTeamId(fixedTeamId || initialTeamId || savedViewState.selectedTeamId);
    setSearch(savedViewState.search);
    setSexFilter(savedViewState.sexFilter);
    setRosterRoleFilter(savedViewState.rosterRoleFilter);
    setAssignedSearch(savedViewState.assignedSearch);
    setAssignedRosterStatusFilter(savedViewState.assignedRosterStatusFilter);
    setAssignedTierFilter(savedViewState.assignedTierFilter);
    setNtrpFilter(savedViewState.ntrpFilter);
    setPartnerSearch(savedViewState.partnerSearch);
    setPartnerCompatibilityFilter(savedViewState.partnerCompatibilityFilter);
    setPartnerPairingRuleFilter(savedViewState.partnerPairingRuleFilter);
    setPartnerCalculationFilter(savedViewState.partnerCalculationFilter);
    setPartnerReviewFilter(savedViewState.partnerReviewFilter);
    setPartnerQuickFilter(savedViewState.partnerQuickFilter);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, fixedTeamId, initialTeamId, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({
      selectedTeamId,
      search,
      sexFilter,
      rosterRoleFilter,
      assignedSearch,
      assignedRosterStatusFilter,
      assignedTierFilter,
      ntrpFilter,
      partnerSearch,
      partnerCompatibilityFilter,
      partnerPairingRuleFilter,
      partnerCalculationFilter,
      partnerReviewFilter,
      partnerQuickFilter,
    });
  }, [assignedRosterStatusFilter, assignedSearch, assignedTierFilter, ntrpFilter, partnerCalculationFilter, partnerCompatibilityFilter, partnerPairingRuleFilter, partnerQuickFilter, partnerReviewFilter, partnerSearch, rosterRoleFilter, search, selectedTeamId, setSavedViewState, sexFilter, viewStateKey, viewStateReady]);

  const loadPlayers = useCallback(async () => {
    const { data, error } = await supabase
      .from('players')
      .select('id, first_name, last_name, email, sex, ntrp_rating, estimated_dynamic_rating, is_active')
      .is('deleted_at', null)
      .eq('is_active', true);

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    setPlayers(((data || []) as unknown as PlayerRow[]).sort((a, b) => comparePlayerNames(a, b, userPreferences)));
  }, [userPreferences]);

  const loadRosterFilterMetadata = useCallback(async () => {
    const { data, error } = await supabase
      .from('seasons')
      .select('id, season_name, year, start_date, end_date')
      .is('deleted_at', null)
      .order('year', { ascending: false });

    if (error) {
      setSnack({ severity: 'error', message: error.message || 'Unable to load roster filters.' });
      return;
    }

    setSeasonRows((data || []) as unknown as SeasonFilterRow[]);
  }, []);

  const loadTeamDefaults = useCallback(async () => {
    const { data, error } = await supabase
      .from('teams')
      .select('id, default_target_match_count_per_player, team_gender_type')
      .is('deleted_at', null);

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    const teams = (data || []) as unknown as TeamDefaultRow[];
    setTeamDefaults(new Map(teams.map((team) => [team.id, team.default_target_match_count_per_player])));
    setTeamGenderTypes(new Map(teams.map((team) => [team.id, team.team_gender_type || 'mixed'])));
  }, []);

  const loadRoster = useCallback(async () => {
    if (!selectedTeamId) {
      setRosterRows([]);
      setPartnerPreferences([]);
      setDrafts({});
      setSelectedAvailable([]);
      setSelectedAssigned([]);
      return;
    }
    setLoading(true);
    const [rosterResult, partnerResult] = await Promise.all([
      supabase
        .from('team_rosters')
        .select(
          'id, team_id, player_id, target_match_count, player_tier, roster_status, roster_role, team_priority, registration_status, registration_verified_at, registration_verified_by, registration_notes, include_substitute_in_fairness, is_active, notes, players!inner(id, first_name, last_name, email, sex, ntrp_rating, estimated_dynamic_rating, is_active)',
        )
        .eq('team_id', selectedTeamId)
        .eq('is_active', true)
        .is('deleted_at', null)
        .eq('players.is_active', true)
        .is('players.deleted_at', null)
        .order('created_at'),
      selectedWorkspaceId
        ? supabase
            .from('partner_preferences')
            .select('id, workspace_id, team_id, player1_id, player2_id, compatibility, pairing_rule, is_active, notes, calculated_compatibility, calculated_confidence, compatibility_source, review_status, review_severity, calculated_score, calculated_match_count, calculated_current_season_record, calculated_average_game_differential, calculated_match_tiebreak_record, calculated_comeback_wins, calculated_trend_direction, calculated_match_story, calculated_metrics, calculated_at')
            .eq('workspace_id', selectedWorkspaceId)
            .is('deleted_at', null)
            .order('created_at')
        : Promise.resolve({ data: [], error: null }),
    ]);

    setLoading(false);
    if (rosterResult.error) {
      setSnack({ severity: 'error', message: rosterResult.error.message || 'Unable to load roster.' });
      return;
    }

    const rows = ((rosterResult.data || []) as unknown as RosterRow[])
      .filter((row) => row.players)
      .sort((a, b) => comparePlayerNames(a.players!, b.players!, userPreferences));
    setRosterRows(rows);
    setDrafts(
      rows.reduce<Record<string, RosterDraft>>((acc, row) => {
        acc[row.id] = {
          target_match_count: isDesignatedSubstitute(row) ? null : row.target_match_count ?? 0,
          player_tier: row.player_tier,
          roster_status: row.roster_status || 'active',
          roster_role: normalizeRosterRole(row.roster_role),
          team_priority: row.team_priority || 'normal',
          registration_status: row.registration_status || 'Unknown',
          notes: row.notes || '',
        };
        return acc;
      }, {}),
    );
    setSelectedAvailable([]);
    setSelectedAssigned([]);
    setPartnerPreferences(partnerResult.error ? [] : ((partnerResult.data || []) as unknown as PartnerPreferenceRow[]));
  }, [selectedTeamId, selectedWorkspaceId, userPreferences]);

  useEffect(() => {
    void loadPlayers();
    void loadRosterFilterMetadata();
    void loadTeamDefaults();
  }, [loadPlayers, loadRosterFilterMetadata, loadTeamDefaults]);

  useEffect(() => {
    if (fixedTeamId) {
      setSelectedTeamId(fixedTeamId);
      return;
    }
    if (lookups.teams.length === 0) {
      setSelectedTeamId('');
      return;
    }
    const requestedTeam = initialTeamId ? lookups.teams.find((team) => team.value === initialTeamId) : null;
    const defaultSeasonId = currentSeasonId || lookups.seasons[0]?.value;
    setSelectedTeamId((current) => {
      if (requestedTeam) return requestedTeam.value;
      if (current && lookups.teams.some((team) => team.value === current)) return current;
      return lookups.teams.find((team) => team.season_id === defaultSeasonId)?.value || lookups.teams[0].value;
    });
  }, [currentSeasonId, fixedTeamId, initialTeamId, lookups.seasons, lookups.teams]);

  useEffect(() => {
    void loadRoster();
  }, [loadRoster]);

  const assignedPlayerIds = useMemo(() => new Set(rosterRows.map((row) => row.player_id)), [rosterRows]);
  const rosterSexCounts = useMemo(() => {
    return rosterRows.reduce(
      (counts, row) => {
        if (row.players?.sex === 'male') counts.men += 1;
        else if (row.players?.sex === 'female') counts.women += 1;
        else counts.unknown += 1;
        return counts;
      },
      { men: 0, women: 0, unknown: 0 },
    );
  }, [rosterRows]);
  const rosterViolations = useMemo(() => {
    if (selectedTeamGenderType === 'mixed') return [];
    const requiredSex = selectedTeamGenderType === 'mens' ? 'male' : 'female';
    return rosterRows.filter((row) => row.players?.sex && row.players.sex !== requiredSex);
  }, [rosterRows, selectedTeamGenderType]);

  const teamAllowsPlayer = (player: PlayerRow): boolean => {
    if (selectedTeamGenderType === 'mixed') return true;
    if (selectedTeamGenderType === 'mens') return player.sex === 'male';
    return player.sex === 'female';
  };

  const selectedTeamTypeLabel = selectedTeamGenderType === 'mens' ? "Men's" : selectedTeamGenderType === 'womens' ? "Women's" : 'Mixed';

  const compareValues = (aValue: string | number | boolean | null | undefined, bValue: string | number | boolean | null | undefined, direction: SortDirection): number => {
    const multiplier = direction === 'asc' ? 1 : -1;
    if (typeof aValue === 'number' || typeof bValue === 'number') return (Number(aValue || 0) - Number(bValue || 0)) * multiplier;
    if (typeof aValue === 'boolean' || typeof bValue === 'boolean') return (Number(Boolean(aValue)) - Number(Boolean(bValue))) * multiplier;
    return String(aValue || '').localeCompare(String(bValue || ''), undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
  };

  const availableSortValue = (player: PlayerRow, field: AvailableSortField): string | number | null => {
    if (field === 'player_name') return formatPlayerName(player, userPreferences);
    if (field === 'ntrp_rating') return player.ntrp_rating;
    if (field === 'estimated_dynamic_rating') return player.estimated_dynamic_rating;
    return player.sex || '';
  };

  const assignedSortValue = (row: RosterRow, field: AssignedSortField): string | number | boolean | null => {
    const player = playerById.get(row.player_id) || row.players || undefined;
    const draft = drafts[row.id];
    if (field === 'player_name') return formatPlayerName(player, userPreferences);
    if (field === 'sex') return player?.sex || '';
    if (field === 'ntrp_rating') return player?.ntrp_rating ?? null;
    if (field === 'estimated_dynamic_rating') return player?.estimated_dynamic_rating ?? null;
    if (field === 'target_match_count') return draft?.target_match_count ?? row.target_match_count ?? 0;
    if (field === 'player_tier') return tierLabels[draft?.player_tier || row.player_tier || ''] || 'No Tier';
    if (field === 'roster_status') return rosterStatusLabels[draft?.roster_status || row.roster_status || ''] || '';
    if (field === 'roster_role') return rosterRoleLabels[normalizeRosterRole(draft?.roster_role || row.roster_role)];
    if (field === 'team_priority') return draft?.team_priority || row.team_priority || 'normal';
    return draft?.registration_status || row.registration_status || 'Unknown';
  };

  const availablePlayers = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return players.filter((player) => {
      if (assignedPlayerIds.has(player.id)) return false;
      if (!teamAllowsPlayer(player)) return false;
      if (sexFilter !== 'all' && player.sex !== sexFilter) return false;
      if (ntrpFilter.length > 0 && !ntrpFilter.includes(ntrpValue(player))) return false;
      if (!normalizedSearch) return true;
      const haystack = `${player.first_name} ${player.last_name} ${player.email || ''}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    }).sort((a, b) => {
      if (userPreferences.groupPlayersBySex) {
        const sexCompare = String(a.sex || '').localeCompare(String(b.sex || ''), undefined, { sensitivity: 'base' });
        if (sexCompare) return sexCompare;
      }
      const result = compareValues(availableSortValue(a, availableSort.field), availableSortValue(b, availableSort.field), availableSort.direction);
      return result || comparePlayerNames(a, b, userPreferences);
    });
  }, [assignedPlayerIds, availableSort, ntrpFilter, players, search, sexFilter, selectedTeamGenderType, userPreferences]);

  const sortedRosterRows = useMemo(() => {
    const normalizedSearch = assignedSearch.trim().toLowerCase();
    return rosterRows.filter((row) => {
      const draft = drafts[row.id];
      const player = playerById.get(row.player_id) || row.players || undefined;
      if (rosterRoleFilter !== 'all' && normalizeRosterRole(draft?.roster_role || row.roster_role) !== rosterRoleFilter) return false;
      if (assignedRosterStatusFilter !== 'all' && (draft?.roster_status || row.roster_status || 'active') !== assignedRosterStatusFilter) return false;
      if (assignedTierFilter !== 'all' && (draft?.player_tier || row.player_tier || '') !== assignedTierFilter) return false;
      if (!normalizedSearch) return true;
      const haystack = `${player?.first_name || ''} ${player?.last_name || ''} ${draft?.notes || row.notes || ''}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    }).sort((a, b) => {
      if (userPreferences.groupPlayersBySex) {
        const sexCompare = String(a.players?.sex || '').localeCompare(String(b.players?.sex || ''), undefined, { sensitivity: 'base' });
        if (sexCompare) return sexCompare;
      }
      const result = compareValues(assignedSortValue(a, assignedSort.field), assignedSortValue(b, assignedSort.field), assignedSort.direction);
      return result || comparePlayerNames(a.players!, b.players!, userPreferences);
    });
  }, [assignedRosterStatusFilter, assignedSearch, assignedSort, assignedTierFilter, drafts, playerById, rosterRoleFilter, rosterRows, userPreferences]);

  const rosterRoleCounts = useMemo(() => {
    return rosterRows.reduce(
      (counts, row) => {
        if (isDesignatedSubstitute(row)) counts.designatedSubstitutes += 1;
        else counts.regularPlayers += 1;
        return counts;
      },
      { regularPlayers: 0, designatedSubstitutes: 0 },
    );
  }, [rosterRows]);

  const rosterSummary = useMemo(() => {
    const activeRows = rosterRows.filter((row) => (drafts[row.id]?.roster_status || row.roster_status || 'active') === 'active');
    const registeredRows = rosterRows.filter((row) => (drafts[row.id]?.registration_status || row.registration_status) === 'Registered');
    const verifiedRows = rosterRows.filter((row) => Boolean(row.registration_verified_at));
    return {
      total: rosterRows.length,
      active: activeRows.length,
      registered: registeredRows.length,
      verified: verifiedRows.length,
    };
  }, [drafts, rosterRows]);

  const selectedAvailableRows = availablePlayers.filter((player) => selectedAvailable.includes(player.id));
  const selectedAssignedRows = rosterRows.filter((row) => selectedAssigned.includes(row.id));

  const toggleAvailableSort = (field: AvailableSortField) => {
    setAvailableSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toggleAssignedSort = (field: AssignedSortField) => {
    setAssignedSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const AvailableSortHeader = ({ field, label }: { field: AvailableSortField; label: string }) => (
    <TableCell sx={{ fontWeight: 700 }}>
      <TableSortLabel active={availableSort.field === field} direction={availableSort.field === field ? availableSort.direction : 'asc'} onClick={() => toggleAvailableSort(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const AssignedSortHeader = ({ field, label, sx }: { field: AssignedSortField; label: string; sx?: object }) => (
    <TableCell sx={{ fontWeight: 700, ...sx }}>
      <TableSortLabel active={assignedSort.field === field} direction={assignedSort.field === field ? assignedSort.direction : 'asc'} onClick={() => toggleAssignedSort(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const suggestedTargetMatchCount = async (): Promise<number> => {
    if (!selectedTeamId) return 0;
    const { count } = await supabase
      .from('match_events')
      .select('id', { count: 'exact', head: true })
      .eq('team_id', selectedTeamId)
      .eq('match_classification', 'regular_season')
      .is('deleted_at', null);
    return count ? Math.ceil(count / 2) : 0;
  };

  const loadSelectedTeamDefaultTarget = async (): Promise<number | null> => {
    if (!selectedTeamId) return null;
    const { data, error } = await supabase
      .from('teams')
      .select('default_target_match_count_per_player, team_gender_type')
      .eq('id', selectedTeamId)
      .is('deleted_at', null)
      .single();

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return selectedTeamDefaultTarget;
    }

    const team = data as unknown as { default_target_match_count_per_player: number | null; team_gender_type: TeamGenderType | null };
    const defaultTarget = team?.default_target_match_count_per_player ?? null;
    const genderType = team?.team_gender_type || 'mixed';
    setTeamDefaults((current) => {
      const next = new Map(current);
      next.set(selectedTeamId, defaultTarget);
      return next;
    });
    setTeamGenderTypes((current) => {
      const next = new Map(current);
      next.set(selectedTeamId, genderType);
      return next;
    });
    return defaultTarget;
  };

  const refreshRosterScreen = async () => {
    await Promise.all([loadRoster(), loadTeamDefaults()]);
  };

  const openPartnerDialog = (preference?: PartnerPreferenceRow) => {
    if (partnerPreferenceReadOnly) return;
    if (preference) {
      setPartnerDraft({
        id: preference.id,
        player1Id: preference.player1_id,
        player2Id: preference.player2_id,
        compatibility: preference.compatibility || 'Neutral',
        pairingRule: normalizePairingRule(preference.pairing_rule),
        isActive: preference.is_active !== false,
        notes: preference.notes || '',
      });
    } else {
      setPartnerDraft({ id: null, player1Id: rosterRows[0]?.player_id || '', player2Id: '', compatibility: 'Unknown', pairingRule: 'none', isActive: true, notes: '' });
    }
    setPartnerDialogOpen(true);
  };

  const closePartnerDialog = () => {
    setPartnerDialogOpen(false);
    setPartnerDraft({ id: null, player1Id: '', player2Id: '', compatibility: 'Unknown', pairingRule: 'none', isActive: true, notes: '' });
  };

  const savePartnerPreference = async () => {
    if (partnerPreferenceReadOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return;
    }
    if (!selectedWorkspaceId || !partnerDraft.player1Id || !partnerDraft.player2Id) {
      setSnack({ severity: 'error', message: 'Choose both players.' });
      return;
    }
    if (
      selectedTeamGenderType === 'mixed'
      && !isMaleFemalePair(playerById.get(partnerDraft.player1Id), playerById.get(partnerDraft.player2Id))
    ) {
      setSnack({ severity: 'error', message: 'Mixed doubles partner preferences must be one male player and one female player.' });
      return;
    }

    let normalized;
    try {
      normalized = normalizedPartnerPreferencePayload(selectedWorkspaceId, partnerDraft.player1Id, partnerDraft.player2Id);
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Choose two different players.' });
      return;
    }

    const nextKey = partnerPreferenceKey(selectedWorkspaceId, normalized.player1_id, normalized.player2_id);
    const payload = {
      ...normalized,
      compatibility: partnerDraft.compatibility,
      ...(partnerDraft.id ? {} : { compatibility_source: 'Captain Set' }),
      pairing_rule: partnerDraft.pairingRule,
      is_active: partnerDraft.isActive,
      notes: partnerDraft.notes.trim() || null,
      updated_by: auditUserId,
    };
    const duplicate = partnerPreferences.find((preference) => preference.id !== partnerDraft.id && partnerPreferenceKey(preference.workspace_id, preference.player1_id, preference.player2_id) === nextKey);
    const targetPreferenceId = partnerDraft.id || duplicate?.id || null;
    const result = targetPreferenceId
      ? await supabase.from('partner_preferences').update(payload).eq('id', targetPreferenceId)
      : await supabase.from('partner_preferences').insert({ ...payload, created_by: auditUserId });

    if (result.error) {
      setSnack({ severity: 'error', message: result.error.message });
      return;
    }

    closePartnerDialog();
    setSnack({ severity: 'success', message: 'Partner preference saved.' });
    await loadRoster();
  };

  const reviewCalculatedCompatibility = async (preference: PartnerPreferenceRow, action: 'accept' | 'keep') => {
    if (partnerPreferenceReadOnly || !preference.calculated_compatibility) return;
    const payload = action === 'accept'
      ? {
          compatibility: preference.calculated_compatibility,
          compatibility_source: 'Captain Confirmed Data',
          review_status: 'accepted',
          review_severity: 'None',
          updated_by: auditUserId,
        }
      : {
          compatibility_source: 'Captain Override',
          review_status: 'overridden',
          review_severity: 'None',
          updated_by: auditUserId,
        };
    const { error } = await supabase.from('partner_preferences').update(payload).eq('id', preference.id);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    await supabase.from('partner_compatibility_visible_history').insert({
      partner_preference_id: preference.id,
      workspace_id: preference.workspace_id,
      player1_id: preference.player1_id,
      player2_id: preference.player2_id,
      previous_visible_compatibility: preference.compatibility || 'Unknown',
      new_visible_compatibility: action === 'accept' ? preference.calculated_compatibility : preference.compatibility || 'Unknown',
      previous_source: preference.compatibility_source || 'Captain Set',
      new_source: action === 'accept' ? 'Captain Confirmed Data' : 'Captain Override',
      calculated_compatibility_at_time: preference.calculated_compatibility,
      review_action: action === 'accept' ? 'Accepted calculated rating' : 'Kept captain rating',
      changed_by: auditUserId,
    });
    setSnack({ severity: 'success', message: action === 'accept' ? 'Calculated rating accepted.' : 'Captain rating kept.' });
    await loadRoster();
  };

  const deletePartnerPreference = async (preference: PartnerPreferenceRow) => {
    if (partnerPreferenceReadOnly) return;
    const { error } = await supabase
      .from('partner_preferences')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: auditUserId,
        delete_reason: 'Deleted from partner preferences UI',
      })
      .eq('id', preference.id);

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    setPartnerPreferences((current) => current.filter((item) => item.id !== preference.id));
    setSnack({ severity: 'success', message: 'Partner preference deleted.' });
  };

  const partnerName = (playerId: string): string => formatPlayerName(playerById.get(playerId), userPreferences) || lookupLabel(lookups, 'players', playerId);

  const sortedPartnerPreferences = useMemo(() => {
    const normalizedSearch = partnerSearch.trim().toLowerCase();
    return partnerPreferences.filter((preference) => {
      if (preference.is_active === false) return false;
      if (!activeRosterPlayerIds.has(preference.player1_id) || !activeRosterPlayerIds.has(preference.player2_id)) return false;
      if (selectedTeamGenderType === 'mixed' && !isMaleFemalePair(playerById.get(preference.player1_id), playerById.get(preference.player2_id))) return false;
      if (partnerCompatibilityFilter !== 'all' && preference.compatibility !== partnerCompatibilityFilter) return false;
      if (partnerPairingRuleFilter !== 'all' && normalizePairingRule(preference.pairing_rule) !== partnerPairingRuleFilter) return false;
      if (partnerCalculationFilter === 'calculated' && !preference.calculated_compatibility) return false;
      if (partnerCalculationFilter === 'not_calculated' && preference.calculated_compatibility) return false;
      if (partnerReviewFilter === 'needs_review' && preference.review_status !== 'needs_review') return false;
      if (partnerReviewFilter === 'accepted' && preference.review_status !== 'accepted') return false;
      if (partnerQuickFilter === 'captain_defined' && !String(preference.compatibility_source || '').toLowerCase().includes('captain')) return false;
      if (partnerQuickFilter === 'calculated' && !preference.calculated_compatibility) return false;
      if (partnerQuickFilter === 'needs_review' && preference.review_status !== 'needs_review') return false;
      if (!normalizedSearch) return true;
      const haystack = `${partnerName(preference.player1_id)} ${partnerName(preference.player2_id)} ${preference.notes || ''}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    }).sort((a, b) => {
      const aNames = normalizePartnerPair(a.player1_id, a.player2_id).map(partnerName).join(' ');
      const bNames = normalizePartnerPair(b.player1_id, b.player2_id).map(partnerName).join(' ');
      return aNames.localeCompare(bNames, undefined, { sensitivity: 'base' });
    });
  }, [activeRosterPlayerIds, partnerCalculationFilter, partnerCompatibilityFilter, partnerPairingRuleFilter, partnerPreferences, partnerQuickFilter, partnerReviewFilter, partnerSearch, playerById, selectedTeamGenderType, userPreferences]);

  const visiblePartnerPreferences = useMemo(() => partnerPreferences.filter((preference) => {
    if (preference.is_active === false) return false;
    if (!activeRosterPlayerIds.has(preference.player1_id) || !activeRosterPlayerIds.has(preference.player2_id)) return false;
    return selectedTeamGenderType !== 'mixed' || isMaleFemalePair(playerById.get(preference.player1_id), playerById.get(preference.player2_id));
  }), [activeRosterPlayerIds, partnerPreferences, playerById, selectedTeamGenderType]);

  const partnerSummary = useMemo(() => ({
    total: visiblePartnerPreferences.length,
    captainDefined: visiblePartnerPreferences.filter((preference) => String(preference.compatibility_source || '').toLowerCase().includes('captain')).length,
    calculated: visiblePartnerPreferences.filter((preference) => Boolean(preference.calculated_compatibility)).length,
    needsReview: visiblePartnerPreferences.filter((preference) => preference.review_status === 'needs_review').length,
  }), [visiblePartnerPreferences]);

  const addSelected = async () => {
    if (readOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return;
    }
    if (!selectedTeamId || selectedAvailableRows.length === 0) return;
    const currentTeamDefaultTarget = await loadSelectedTeamDefaultTarget();
    const invalidPlayers = selectedAvailableRows.filter((player) => !teamAllowsPlayer(player));
    if (invalidPlayers.length > 0) {
      setSnack({
        severity: 'error',
        message: `${selectedTeamTypeLabel} teams can only add ${selectedTeamGenderType === 'mens' ? 'male' : 'female'} players.`,
      });
      return;
    }
    const targetMatchCount = addRosterRole === 'designated_substitute' ? null : currentTeamDefaultTarget ?? (await suggestedTargetMatchCount());
    const payload = selectedAvailableRows.map((player) => ({
      team_id: selectedTeamId,
      player_id: player.id,
      target_match_count: targetMatchCount,
      player_tier: null,
      roster_status: 'active',
      roster_role: addRosterRole,
      registration_status: 'Unknown',
      include_substitute_in_fairness: false,
      is_active: true,
    }));

    const { error } = await supabase.from('team_rosters').insert(payload);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    setAddDialogOpen(false);
    setAddRosterRole('regular');
    setSelectedAvailable([]);
    try {
      const feeAssignmentCount = await syncTeamFeeAssignments(selectedTeamId, selectedAvailableRows.map((player) => player.id), undefined, auditUserId);
      const rosterMessage = `${payload.length} player${payload.length === 1 ? '' : 's'} added to roster.`;
      if (feeAssignmentCount > 0 && selectedAvailableRows.length === 1) {
        setSnack({ severity: 'success', message: `${feeAssignmentCount} active fee assignment${feeAssignmentCount === 1 ? '' : 's'} created for ${formatPlayerName(selectedAvailableRows[0], userPreferences)}.` });
      } else if (feeAssignmentCount > 0) {
        setSnack({ severity: 'success', message: `${rosterMessage} ${feeAssignmentCount} active fee assignment${feeAssignmentCount === 1 ? '' : 's'} created.` });
      } else {
        setSnack({ severity: 'success', message: rosterMessage });
      }
    } catch (syncError) {
      setSnack({ severity: 'error', message: `Roster saved, but fee assignment sync failed: ${feeAssignmentSyncErrorMessage(syncError)}` });
    }
    await loadRoster();
    await refreshLookups();
  };

  const removeSelected = async () => {
    if (readOnly) return;
    if (selectedAssignedRows.length === 0) return;
    const { error } = await supabase
      .from('team_rosters')
      .update({
        is_active: false,
        deleted_at: new Date().toISOString(),
        delete_reason: 'Removed from roster in Phase 1 UI',
      })
      .in('id', selectedAssignedRows.map((row) => row.id));

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    setSnack({ severity: 'success', message: `${selectedAssignedRows.length} roster assignment${selectedAssignedRows.length === 1 ? '' : 's'} removed.` });
    await loadRoster();
  };

  const updateDraft = (rowId: string, patch: Partial<RosterDraft>) => {
    setDrafts((current) => ({ ...current, [rowId]: { ...current[rowId], ...patch } }));
  };

  const setRowSaving = (rowId: string, saving: boolean) => {
    setSavingRosterIds((current) => {
      const next = new Set(current);
      if (saving) {
        next.add(rowId);
      } else {
        next.delete(rowId);
      }
      return next;
    });
  };

  const saveRosterPatch = async (row: RosterRow, patch: RosterPatch, successMessage = 'Roster assignment saved.') => {
    if (readOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return false;
    }
    setRowSaving(row.id, true);
    const { error } = await supabase.from('team_rosters').update(patch).eq('id', row.id);
    setRowSaving(row.id, false);

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return false;
    }

    setRosterRows((current) => current.map((item) => (item.id === row.id ? { ...item, ...patch } : item)));
    setDrafts((current) => ({ ...current, [row.id]: { ...current[row.id], ...patch } }));
    setSnack({ severity: 'success', message: successMessage });
    return true;
  };

  const saveTargetMatchCount = async (row: RosterRow) => {
    if (isDesignatedSubstitute(row)) return;
    const draft = drafts[row.id];
    if (!draft) return;
    const nextTarget = draft.target_match_count ?? null;
    if ((row.target_match_count ?? null) === nextTarget) return;
    await saveRosterPatch(row, { target_match_count: nextTarget }, 'Target saved.');
  };

  const saveRosterRole = async (row: RosterRow, nextRole: RosterRole) => {
    const patch: RosterPatch = { roster_role: nextRole };
    if (nextRole === 'designated_substitute') {
      patch.target_match_count = null;
    } else if (isDesignatedSubstitute(row)) {
      patch.target_match_count = selectedTeamDefaultTarget ?? 0;
    }
    updateDraft(row.id, {
      roster_role: nextRole,
      target_match_count: patch.target_match_count !== undefined ? patch.target_match_count : drafts[row.id]?.target_match_count ?? row.target_match_count ?? 0,
    });
    await saveRosterPatch(row, patch, 'Roster role saved.');
  };

  const saveNotes = async (row: RosterRow) => {
    const draft = drafts[row.id];
    if (!draft) return;
    const nextNotes = draft.notes?.trim() || null;
    if ((row.notes || null) === nextNotes) return;
    await saveRosterPatch(row, { notes: nextNotes }, 'Notes saved.');
  };
  const showRosterAssignments = view === 'all' || view === 'roster';
  const showPartnerPreferences = canViewPartnerPreferences && (view === 'all' || view === 'partner_preferences');
  const partnerPreferenceReadOnly = readOnly || !canEditPartnerPreferences;

  return (
    <Stack spacing={2}>
      {!embedded && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Team Rosters
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Build team rosters from the active player pool and manage roster-specific fairness settings.
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void refreshRosterScreen()}>
            Refresh
          </Button>
        </Box>
      )}

      {!embedded && (
        <Box sx={{ maxWidth: 420 }}>
          <SeasonTeamMultiSelect
            lookups={lookups}
            value={selectedTeamId ? [selectedTeamId] : []}
            onChange={(teamIds) => setSelectedTeamId(teamIds[0] || '')}
            multiple={false}
          />
        </Box>
      )}

      {showRosterAssignments && <PlayerDisplayControls userPreferences={userPreferences} onChange={onUserPreferencesChange} />}

      {selectedTeamId && showRosterAssignments && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Roster</Typography>
          <Stack spacing={0.5} sx={{ mt: 0.75 }}>
            <Typography variant="body2" color="text.secondary">
              Team type: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{selectedTeamTypeLabel}</Box>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Roster composition:{' '}
              <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {rosterSexCounts.men} Men / {rosterSexCounts.women} Women
                {rosterSexCounts.unknown > 0 ? ` / ${rosterSexCounts.unknown} Unknown` : ''}
              </Box>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Roster roles:{' '}
              <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {rosterRoleCounts.regularPlayers} Regular Player{rosterRoleCounts.regularPlayers === 1 ? '' : 's'} / {rosterRoleCounts.designatedSubstitutes} Designated Substitute{rosterRoleCounts.designatedSubstitutes === 1 ? '' : 's'}
              </Box>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Team default target matches per player: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{selectedTeamDefaultTarget ?? 'Not set'}</Box>
            </Typography>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(6, minmax(0, 1fr))' }, gap: 1.5, mt: 2 }}>
            {[
              ['Total Players', rosterSummary.total, `${selectedAvailable.length} pending`],
              ['Regular Players', rosterRoleCounts.regularPlayers, `${rosterSummary.total ? Math.round((rosterRoleCounts.regularPlayers / rosterSummary.total) * 100) : 0}%`],
              ['Designated Subs', rosterRoleCounts.designatedSubstitutes, `${rosterSummary.total ? Math.round((rosterRoleCounts.designatedSubstitutes / rosterSummary.total) * 100) : 0}%`],
              ['Active', rosterSummary.active, `${rosterSummary.total ? Math.round((rosterSummary.active / rosterSummary.total) * 100) : 0}%`],
              ['Registered', rosterSummary.registered, `${rosterSummary.total ? Math.round((rosterSummary.registered / rosterSummary.total) * 100) : 0}%`],
              ['Verified', rosterSummary.verified, `${rosterSummary.total ? Math.round((rosterSummary.verified / rosterSummary.total) * 100) : 0}%`],
            ].map(([label, value, helper]) => (
              <Paper key={label} variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="h5" color="primary" sx={{ fontWeight: 800 }}>{value}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
                <Typography variant="caption" color="text.secondary">{helper}</Typography>
              </Paper>
            ))}
          </Box>
        </Paper>
      )}

      {lookups.teams.length === 0 && <Alert severity="info">Create a team before building a roster.</Alert>}
      {rosterViolations.length > 0 && (
        <Alert severity="warning">
          {rosterViolations.length} active roster assignment{rosterViolations.length === 1 ? '' : 's'} do not match this team&apos;s gender type. Existing rows are preserved; remove or deactivate them when ready.
        </Alert>
      )}

      {showRosterAssignments && (
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Assigned Players
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {rosterRows.length} active roster assignment{rosterRows.length === 1 ? '' : 's'} • {rosterRoleCounts.regularPlayers} regular • {rosterRoleCounts.designatedSubstitutes} designated substitute{rosterRoleCounts.designatedSubstitutes === 1 ? '' : 's'}
              </Typography>
            </Box>
            {!readOnly && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}>
                <Button variant="contained" startIcon={<AddIcon />} disabled={!selectedTeamId} onClick={() => setAddDialogOpen(true)}>
                  Add Players
                </Button>
                <Button color="error" variant="outlined" startIcon={<RemoveIcon />} disabled={selectedAssigned.length === 0} onClick={() => void removeSelected()}>
                  Remove Selected
                </Button>
              </Stack>
            )}
          </Box>
          {loading && <LinearProgress />}
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr 1fr auto' }, gap: 1.5, alignItems: 'center' }}>
            <TextField size="small" label="Search players" value={assignedSearch} onChange={(event) => setAssignedSearch(event.target.value)} />
            <FormControl size="small">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={assignedRosterStatusFilter} onChange={(event) => setAssignedRosterStatusFilter(event.target.value)}>
                <MenuItem value="all">All</MenuItem>
                {rosterStatusOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>Tier</InputLabel>
              <Select label="Tier" value={assignedTierFilter} onChange={(event) => setAssignedTierFilter(event.target.value)}>
                <MenuItem value="all">All</MenuItem>
                {playerTierOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
            {[
              { value: 'all' as const, label: 'All Players' },
              { value: 'regular' as const, label: 'Regular Players' },
              { value: 'designated_substitute' as const, label: 'Designated Substitutes' },
            ].map((option) => (
              <Chip
                key={option.value}
                clickable
                color={rosterRoleFilter === option.value ? 'primary' : 'default'}
                variant={rosterRoleFilter === option.value ? 'filled' : 'outlined'}
                label={option.label}
                onClick={() => setRosterRoleFilter(option.value)}
              />
            ))}
            </Stack>
          </Box>
          <TableContainer sx={{ maxHeight: 620 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {!readOnly && <TableCell padding="checkbox" />}
                  <AssignedSortHeader field="player_name" label="Player" sx={{ minWidth: 170 }} />
                  <AssignedSortHeader field="sex" label="Gender" />
                  <AssignedSortHeader field="ntrp_rating" label="NTRP" />
                  <AssignedSortHeader field="estimated_dynamic_rating" label="EDR" />
                  <AssignedSortHeader field="roster_role" label="Roster Role" sx={{ minWidth: 170 }} />
                  <AssignedSortHeader field="team_priority" label="Team Priority" sx={{ minWidth: 130 }} />
                  <AssignedSortHeader field="target_match_count" label="Target" sx={{ minWidth: 90 }} />
                  <AssignedSortHeader field="player_tier" label="Tier" sx={{ minWidth: 130 }} />
                  <AssignedSortHeader field="roster_status" label="Roster Status" sx={{ minWidth: 140 }} />
                  <AssignedSortHeader field="registration_status" label="Registration Status" sx={{ minWidth: 170 }} />
                  <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>Verified</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRosterRows.map((row) => {
                  const player = playerById.get(row.player_id) || row.players || undefined;
                  const draft = drafts[row.id];
                  return (
                    <TableRow key={row.id} hover>
                      {!readOnly && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedAssigned.includes(row.id)}
                            onChange={(event) =>
                              setSelectedAssigned((current) =>
                                event.target.checked ? [...current, row.id] : current.filter((id) => id !== row.id),
                              )
                            }
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatPlayerName(player, userPreferences)}
                        </Typography>
                        {isDesignatedSubstitute(row) && <Chip size="small" variant="outlined" color="info" label="Designated Substitute" sx={{ mt: 0.5 }} />}
                      </TableCell>
                      <TableCell>{player?.sex || ''}</TableCell>
                      <TableCell>{player ? ntrpValue(player) : ''}</TableCell>
                      <TableCell>{formatEdr(player?.estimated_dynamic_rating)}</TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={normalizeRosterRole(draft?.roster_role || row.roster_role)}
                          disabled={readOnly || savingRosterIds.has(row.id)}
                          onChange={(event) => void saveRosterRole(row, event.target.value as RosterRole)}
                        >
                          {rosterRoleOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={draft?.team_priority || row.team_priority || 'normal'}
                          disabled={readOnly || savingRosterIds.has(row.id)}
                          onChange={(event) => {
                            const teamPriority = event.target.value as 'primary' | 'normal' | 'secondary';
                            updateDraft(row.id, { team_priority: teamPriority });
                            void saveRosterPatch(row, { team_priority: teamPriority }, 'Team priority saved.');
                          }}
                        >
                          {teamPriorityOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={draft?.target_match_count ?? ''}
                          onChange={(event) => updateDraft(row.id, { target_match_count: event.target.value === '' ? null : Number(event.target.value) })}
                          onBlur={() => void saveTargetMatchCount(row)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.currentTarget.blur();
                            }
                          }}
                          disabled={readOnly || savingRosterIds.has(row.id) || isDesignatedSubstitute(row)}
                          placeholder={isDesignatedSubstitute(row) ? 'N/A' : undefined}
                          sx={{ width: 84 }}
                        />
                        {isDesignatedSubstitute(row) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            No target
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={draft?.player_tier || ''}
                          disabled={readOnly || savingRosterIds.has(row.id)}
                          onChange={(event) => {
                            const playerTier = event.target.value || null;
                            updateDraft(row.id, { player_tier: playerTier });
                            void saveRosterPatch(row, { player_tier: playerTier }, 'Player tier saved.');
                          }}
                        >
                          {playerTierOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={draft?.roster_status || 'active'}
                          disabled={readOnly || savingRosterIds.has(row.id)}
                          onChange={(event) => {
                            const rosterStatus = event.target.value;
                            updateDraft(row.id, { roster_status: rosterStatus });
                            void saveRosterPatch(row, { roster_status: rosterStatus }, 'Roster status saved.');
                          }}
                        >
                          {rosterStatusOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={draft?.registration_status || 'Unknown'}
                          disabled={readOnly || savingRosterIds.has(row.id)}
                          onChange={(event) => {
                            const registrationStatus = event.target.value;
                            updateDraft(row.id, { registration_status: registrationStatus });
                            const patch: RosterPatch = { registration_status: registrationStatus };
                            if (registrationStatus === 'Registered') {
                              patch.registration_verified_at = row.registration_verified_at || new Date().toISOString();
                              patch.registration_verified_by = row.registration_verified_by || auditUserId;
                            } else {
                              patch.registration_verified_at = null;
                              patch.registration_verified_by = null;
                            }
                            void saveRosterPatch(row, patch, 'Registration status saved.');
                          }}
                        >
                          {registrationStatusOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{registrationVerifiedLabel(row.registration_verified_at)}</Typography>
                        {row.registration_verified_by && (
                          <Typography variant="caption" color="text.secondary">
                            {lookupLabel(lookups, 'app_users', row.registration_verified_by)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={draft?.notes || ''}
                          onChange={(event) => updateDraft(row.id, { notes: event.target.value })}
                          onBlur={() => void saveNotes(row)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.currentTarget.blur();
                            }
                          }}
                          disabled={readOnly || savingRosterIds.has(row.id)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {sortedRosterRows.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={readOnly ? 11 : 12}>
                      <Stack spacing={1.5} sx={{ py: 5, alignItems: 'center', textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {rosterRows.length === 0 ? 'No players assigned to this roster yet.' : 'No roster assignments match this filter.'}
                        </Typography>
                        {!readOnly && (
                          <Button variant="contained" startIcon={<AddIcon />} disabled={!selectedTeamId} onClick={() => setAddDialogOpen(true)}>
                            Add Players
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Alert severity="success" sx={{ borderRadius: 0 }}>
            Changes are saved automatically.
          </Alert>
        </Paper>
      )}

      {showPartnerPreferences && (
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Partner Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Workspace-wide doubles compatibility notes for rostered players on {selectedTeamName}. These carry forward into future rosters.
            </Typography>
          </Box>
          {!partnerPreferenceReadOnly && (
            <Button variant="contained" startIcon={<AddIcon />} disabled={!selectedWorkspaceId || rosterPreferencePlayers.length < 2} onClick={() => openPartnerDialog()}>
              Add Preference
            </Button>
          )}
        </Box>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
            {[
              ['Active Pairings', partnerSummary.total, selectedTeamGenderType === 'mixed' ? 'Male/female only' : 'Rostered players'],
              ['Captain Defined', partnerSummary.captainDefined, 'Manual preferences'],
              ['Calculated', partnerSummary.calculated, 'Data-driven ratings'],
              ['Needs Review', partnerSummary.needsReview, 'Pending captain decision'],
            ].map(([label, value, helper]) => (
              <Paper key={label} variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="h5" color="primary" sx={{ fontWeight: 800 }}>{value}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
                <Typography variant="caption" color="text.secondary">{helper}</Typography>
              </Paper>
            ))}
          </Box>
        </Box>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr 1fr 1fr 1fr' }, gap: 1.5, alignItems: 'center' }}>
          <TextField size="small" label="Search pairings" value={partnerSearch} onChange={(event) => setPartnerSearch(event.target.value)} />
          <FormControl size="small">
            <InputLabel>Compatibility</InputLabel>
            <Select label="Compatibility" value={partnerCompatibilityFilter} onChange={(event) => setPartnerCompatibilityFilter(event.target.value)}>
              <MenuItem value="all">All</MenuItem>
              {compatibilityLevels.map((level) => <MenuItem key={level} value={level}>{level}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Scheduling</InputLabel>
            <Select label="Scheduling" value={partnerPairingRuleFilter} onChange={(event) => setPartnerPairingRuleFilter(event.target.value)}>
              <MenuItem value="all">All</MenuItem>
              {pairingRuleLevels.map((level) => <MenuItem key={level} value={level}>{pairingRuleLabels[level]}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Calculated</InputLabel>
            <Select label="Calculated" value={partnerCalculationFilter} onChange={(event) => setPartnerCalculationFilter(event.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="calculated">Calculated</MenuItem>
              <MenuItem value="not_calculated">Not Calculated</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Review</InputLabel>
            <Select label="Review" value={partnerReviewFilter} onChange={(event) => setPartnerReviewFilter(event.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="needs_review">Needs Review</MenuItem>
              <MenuItem value="accepted">Accepted</MenuItem>
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gridColumn: '1 / -1' }}>
            {[
              ['all', 'All Pairings'],
              ['captain_defined', 'Captain Defined'],
              ['calculated', 'Calculated'],
              ['needs_review', 'Needs Review'],
            ].map(([value, label]) => (
              <Chip
                key={value}
                clickable
                color={partnerQuickFilter === value ? 'primary' : 'default'}
                variant={partnerQuickFilter === value ? 'filled' : 'outlined'}
                label={label}
                onClick={() => setPartnerQuickFilter(value)}
              />
            ))}
          </Stack>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Player 1</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Player 2</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Compatibility</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Calculated</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Evidence</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Review</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Scheduling Preference</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                {!partnerPreferenceReadOnly && <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedPartnerPreferences.map((preference) => (
                <TableRow key={preference.id} hover>
                  <TableCell>{partnerName(preference.player1_id)}</TableCell>
                  <TableCell>{partnerName(preference.player2_id)}</TableCell>
                  <TableCell>
                    <Chip size="small" color={compatibilityChipColor(preference.compatibility)} label={preference.compatibility || 'Unknown'} />
                    {preference.compatibility_source && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {preference.compatibility_source}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {preference.calculated_compatibility ? (
                      <Tooltip title={preference.calculated_match_story || 'Calculated from completed doubles match history.'}>
                        <Stack spacing={0.35} alignItems="flex-start">
                          <Chip size="small" color={compatibilityChipColor(preference.calculated_compatibility)} label={`${preference.calculated_compatibility}${preference.calculated_score != null ? ` • ${Number(preference.calculated_score).toFixed(1)}` : ''}`} />
                          <Typography variant="caption" color="text.secondary">
                            {preference.calculated_confidence || 'No confidence'} confidence
                          </Typography>
                        </Stack>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">Not calculated</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {preference.calculated_match_count ? (
                      <Typography variant="body2">
                        {preference.calculated_match_count} matches
                        {allTimePairingRecord(preference) ? ` • All-time ${allTimePairingRecord(preference)}` : ''}
                        {currentSeasonPairingRecord(preference) ? ` • Current season ${currentSeasonPairingRecord(preference)}` : ''}
                        {preference.calculated_average_game_differential != null ? ` • ${Number(preference.calculated_average_game_differential) >= 0 ? '+' : ''}${Number(preference.calculated_average_game_differential).toFixed(1)} games` : ''}
                        {preference.calculated_match_tiebreak_record ? ` • MTB ${preference.calculated_match_tiebreak_record}` : ''}
                        {preference.calculated_trend_direction ? ` • ${preference.calculated_trend_direction}` : ''}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">Insufficient evidence</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {preference.review_status === 'needs_review' ? (
                      <Stack spacing={0.75} alignItems="flex-start">
                        <Chip size="small" color="warning" label={`Needs review${preference.review_severity ? ` • ${preference.review_severity}` : ''}`} />
                        <Typography variant="caption" color="text.secondary">
                          Accept changes compatibility: {preference.compatibility || 'Unknown'} → {preference.calculated_compatibility || 'Unknown'}
                        </Typography>
                        {!partnerPreferenceReadOnly && (
                          <Stack direction="row" spacing={0.5}>
                            <Button size="small" variant="outlined" onClick={() => void reviewCalculatedCompatibility(preference, 'accept')}>Accept {preference.calculated_compatibility || 'calculated'}</Button>
                            <Button size="small" onClick={() => void reviewCalculatedCompatibility(preference, 'keep')}>Keep</Button>
                          </Stack>
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">{preference.review_status && preference.review_status !== 'none' ? preference.review_status : '-'}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={pairingRuleHelpText[normalizePairingRule(preference.pairing_rule)]}>
                      <Chip size="small" color={pairingRuleChipColor(preference.pairing_rule)} label={pairingRuleLabels[normalizePairingRule(preference.pairing_rule)]} />
                    </Tooltip>
                  </TableCell>
                  <TableCell>{preference.is_active === false ? 'No' : 'Yes'}</TableCell>
                  <TableCell>{preference.notes || '-'}</TableCell>
                  {!partnerPreferenceReadOnly && (
                    <TableCell align="right">
                      <Tooltip title="Edit preference">
                        <IconButton
                          size="small"
                          aria-label={`Edit partner preference for ${partnerName(preference.player1_id)} and ${partnerName(preference.player2_id)}`}
                          onClick={() => openPartnerDialog(preference)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete preference">
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={`Delete partner preference for ${partnerName(preference.player1_id)} and ${partnerName(preference.player2_id)}`}
                          onClick={() => void deletePartnerPreference(preference)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {sortedPartnerPreferences.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={partnerPreferenceReadOnly ? 9 : 10}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No partner preferences saved for players on this roster.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      {showRosterAssignments && (
      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setAddRosterRole('regular');
          setSelectedAvailable([]);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Add Players to {selectedTeamName}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField label="Search name or email" value={search} onChange={(event) => setSearch(event.target.value)} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Roster Role</InputLabel>
              <Select label="Roster Role" value={addRosterRole} onChange={(event) => setAddRosterRole(event.target.value as RosterRole)}>
                {rosterRoleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {addRosterRole === 'designated_substitute' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                  Designated Substitutes are added without target matches and are excluded from normal lineup recommendations.
                </Typography>
              )}
            </FormControl>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select label="Gender" value={sexFilter} onChange={(event) => setSexFilter(event.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  {sexOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>NTRP</InputLabel>
                <Select
                  multiple
                  label="NTRP"
                  value={ntrpFilter}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  onChange={(event) => {
                    const value = event.target.value;
                    setNtrpFilter(typeof value === 'string' ? value.split(',') : value);
                  }}
                >
                  {ntrpOptions.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TableContainer sx={{ maxHeight: 520, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <AvailableSortHeader field="player_name" label="Player" />
                    <AvailableSortHeader field="sex" label="Gender" />
                    <AvailableSortHeader field="ntrp_rating" label="NTRP" />
                    <AvailableSortHeader field="estimated_dynamic_rating" label="EDR" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {availablePlayers.map((player) => (
                    <TableRow key={player.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedAvailable.includes(player.id)}
                          onChange={(event) =>
                            setSelectedAvailable((current) =>
                              event.target.checked ? [...current, player.id] : current.filter((id) => id !== player.id),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatPlayerName(player, userPreferences)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {player.email || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>{player.sex || ''}</TableCell>
                      <TableCell>{ntrpValue(player)}</TableCell>
                      <TableCell>{formatEdr(player.estimated_dynamic_rating)}</TableCell>
                    </TableRow>
                  ))}
                  {availablePlayers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                          No available players found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddDialogOpen(false);
              setAddRosterRole('regular');
              setSelectedAvailable([]);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" disabled={selectedAvailableRows.length === 0} onClick={() => void addSelected()}>
            Add Selected Players
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {showPartnerPreferences && (
      <Dialog open={partnerDialogOpen} onClose={closePartnerDialog} fullWidth maxWidth="sm">
        <DialogTitle>{partnerDraft.id ? 'Edit Partner Preference' : 'Add Partner Preference'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Player</InputLabel>
              <Select label="Player" value={partnerDraft.player1Id} onChange={(event) => setPartnerDraft((current) => ({ ...current, player1Id: event.target.value }))}>
                {rosterPreferencePlayers.map((player) => (
                  <MenuItem key={player.id} value={player.id}>
                    {formatPlayerName(player, userPreferences)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Partner</InputLabel>
              <Select label="Partner" value={partnerDraft.player2Id} onChange={(event) => setPartnerDraft((current) => ({ ...current, player2Id: event.target.value }))}>
                {rosterPreferencePlayers.map((player) => (
                  <MenuItem key={player.id} value={player.id}>
                    {formatPlayerName(player, userPreferences)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Compatibility</InputLabel>
              <Select label="Compatibility" value={partnerDraft.compatibility} onChange={(event) => setPartnerDraft((current) => ({ ...current, compatibility: event.target.value as PartnerCompatibility }))}>
                {compatibilityLevels.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                Measures how successful this partnership has historically been.
              </Typography>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Scheduling Preference</InputLabel>
              <Select label="Scheduling Preference" value={partnerDraft.pairingRule} onChange={(event) => setPartnerDraft((current) => ({ ...current, pairingRule: event.target.value as PartnerPairingRule }))}>
                {pairingRuleLevels.map((rule) => (
                  <MenuItem key={rule} value={rule}>
                    <Box>
                      <Typography variant="body2">{pairingRuleLabels[rule]}</Typography>
                      <Typography variant="caption" color="text.secondary">{pairingRuleHelpText[rule]}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                Controls how strongly CourtCaptain should keep these players together (or apart) when building lineups.
              </Typography>
            </FormControl>
            {partnerDraft.pairingRule !== 'none' && (
              <Alert severity={['required_when_both_selected', 'schedule_only_as_pair', 'excluded'].includes(partnerDraft.pairingRule) ? 'warning' : partnerDraft.pairingRule === 'avoid_pairing' ? 'warning' : 'info'}>
                {pairingRuleHelpText[partnerDraft.pairingRule]}
              </Alert>
            )}
            <FormControlLabel
              control={<Checkbox checked={partnerDraft.isActive} onChange={(event) => setPartnerDraft((current) => ({ ...current, isActive: event.target.checked }))} />}
              label="Active relationship"
            />
            <TextField label="Notes" value={partnerDraft.notes} onChange={(event) => setPartnerDraft((current) => ({ ...current, notes: event.target.value }))} multiline minRows={3} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePartnerDialog}>Cancel</Button>
          <Button variant="contained" onClick={() => void savePartnerPreference()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      )}

      <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}
